import { useLayoutEffect, type RefObject } from 'react';

import {
  useBoardElementRectStore,
  useWorkspaceUiStore,
  type BoardElementId,
} from '@walls/store';
import { useBoardViewportRef } from '../viewport/BoardViewportContext';

const scrollableOverflowValues = new Set(['auto', 'overlay', 'scroll']);

export function useBoardElementRect(
  elementId: BoardElementId,
  elementRef: RefObject<HTMLElement | null>,
  dependencies: unknown[] = []
) {
  const viewportRef = useBoardViewportRef();
  const zoom = useWorkspaceUiStore((state) => state.zoom);
  const removeElementRect = useBoardElementRectStore((state) => state.removeElementRect);
  const setElementRect = useBoardElementRectStore((state) => state.setElementRect);

  useLayoutEffect(() => {
    const element = elementRef.current;
    const viewport = viewportRef?.current;

    if (!element || !viewport) {
      return undefined;
    }

    let pendingMeasureFrame: number | null = null;

    const measure = () => {
      const elementRect = element.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();

      setElementRect(elementId, {
        height: elementRect.height / zoom,
        width: elementRect.width / zoom,
        x: viewport.scrollLeft + (elementRect.left - viewportRect.left) / zoom,
        y: viewport.scrollTop + (elementRect.top - viewportRect.top) / zoom,
      });
    };
    const measureInAnimationFrame = () => {
      pendingMeasureFrame = null;
      measure();
    };
    const scheduleMeasure = () => {
      if (pendingMeasureFrame != null) {
        return;
      }

      pendingMeasureFrame = requestAnimationFrame(measureInAnimationFrame);
    };

    measure();

    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(element);
    const scrollableAncestors = getScrollableAncestors(element, viewport);
    scrollableAncestors.forEach((ancestor) => {
      ancestor.addEventListener('scroll', scheduleMeasure, { passive: true });
    });

    return () => {
      if (pendingMeasureFrame != null) {
        cancelAnimationFrame(pendingMeasureFrame);
      }

      resizeObserver.disconnect();
      scrollableAncestors.forEach((ancestor) => {
        ancestor.removeEventListener('scroll', scheduleMeasure);
      });
      removeElementRect(elementId);
    };
    // dependencies intentionally let callers remeasure after model-driven layout changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId, elementRef, removeElementRect, setElementRect, viewportRef, zoom, ...dependencies]);
}

export function getScrollableAncestors(element: HTMLElement, stopAt: HTMLElement) {
  const ancestors: HTMLElement[] = [];
  let ancestor = element.parentElement;

  while (ancestor) {
    if (isScrollableElement(ancestor)) {
      ancestors.push(ancestor);
    }

    if (ancestor === stopAt) {
      break;
    }

    ancestor = ancestor.parentElement;
  }

  return ancestors;
}

function isScrollableElement(element: HTMLElement) {
  const style = getComputedStyle(element);

  return (
    scrollableOverflowValues.has(style.overflow) ||
    scrollableOverflowValues.has(style.overflowX) ||
    scrollableOverflowValues.has(style.overflowY)
  );
}
